import React, { useState, useEffect } from 'react';
import { Card } from '../../../../../components/Card/Card';
import { Event, changeEventVenueLayout } from '../../../../../model/event';
import { VenueLayout } from '../../../../../model/venue';
import { PermissionButton } from '../../../../../components/Button/PermissionButton';
import { Modal } from '../../../../../components/Modal/Modal';
import { Select } from '../../../../../components/Form/Select/Select';
import { Form } from '../../../../../components/Form/Form';
import { Button } from '../../../../../components/Button/Button';
import { api } from '../../../../../model/store';
import { sortBy } from 'lodash';
import { KeyValueTable } from '../../../../../components/KeyValueTable/KeyValueTable';
import { handleError } from '../../../../../model/notification';
import { AsyncSearch } from '../../../../../components/Form/AsyncSearch/AsyncSearch';
import { t } from '../../../../../locales/translate';
import { Stack } from '../../../../../components/Stack/Stack';
import { venueLayoutPath, venuePath } from '../../../../../routes';
import { Link } from 'react-router-dom';

interface Props {
  event: Event;
}

interface VenuesPatch {
  venue: string;
  venueLayout?: string;
}

export function Venue({ event }: Props): JSX.Element {
  const [model, setModel] = useState<VenuesPatch>({
    venue: (event.venue && event.venue.id) || '',
    venueLayout: '',
  });

  const [modal, setModal] = useState(false);
  const [venueLayouts, setVenueLayouts] = useState<VenueLayout[]>([]);
  const [loadingVenueLayouts, setLoadingVenueLayouts] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  async function loadVenueLayouts(id: string): Promise<void> {
    setLoadingVenueLayouts(true);
    try {
      const resp = await api.indexVenueLayouts({ venueId: id, singleGeneralAdmission: true });
      setVenueLayouts(resp.data);
    } catch (error) {
      handleError(error);
    }
    setLoadingVenueLayouts(false);
  }

  useEffect(() => {
    if (model.venue) {
      loadVenueLayouts(model.venue);
      setModel({
        ...model,
        venueLayout: model.venueLayout ? undefined : event.venueLayout.id,
      });
    }
  }, [model.venue]);

  function handleOnInput(changes: VenuesPatch): void {
    setModel({
      ...model,
      ...changes,
    });
  }

  async function handleOnSubmit(m: VenuesPatch): Promise<void> {
    setLoadingSave(true);
    try {
      if (m.venueLayout) {
        await changeEventVenueLayout({ event, venueLayoutId: m.venueLayout });
      }
      setModal(false);
    } catch (error) {
      handleError(error);
    }
    setLoadingSave(false);
  }

  return (
    <section>
      <Card
        title={t.model.venue._name}
        actions={
          <PermissionButton name="edit" model={event} permission="update_venue" onClick={() => setModal(true)} />
        }
      >
        <KeyValueTable
          items={[
            {
              key: t.model.venue._name,
              value: event.venue && <Link to={venuePath(event.venue.slug)}>{event.venue.name}</Link>,
            },
            {
              key: t.model.venueLayout._name,
              value: event.venueLayout && (
                <Link to={venueLayoutPath(event.venueLayout.id)}>{event.venueLayout.name}</Link>
              ),
            },
          ]}
        />
      </Card>
      <Form onSubmit={handleOnSubmit} model={model} onInput={handleOnInput}>
        <Modal
          open={modal}
          title={t.actions.edit(t.model.venue._name)}
          size="small"
          footer={
            <>
              <Button
                title={t.actions.close('')}
                onClick={() => {
                  setModal(false);
                }}
              />
              <Button
                disabled={!model.venue || !model.venueLayout}
                isSubmit
                title={t.actions.save('')}
                type="primary"
                loading={loadingSave}
              />
            </>
          }
        >
          <Stack>
            <AsyncSearch
              name="venue"
              label={t.model.venue._plural}
              searchIn={['venue_with_single_general_admission']}
              required
            />
            <Select
              label={t.model.venueLayout._name}
              name="venueLayout"
              options={sortBy(venueLayouts, 'name')
                .filter((vl) => vl.archived === false && vl.cloned === false)
                .map((e) => ({
                  value: e.id,
                  label: e.name,
                }))}
              allowEmpty
              loading={loadingVenueLayouts}
              loadingMsg={t.actions.loading(t.model.venueLayout._plural)}
            />
          </Stack>
        </Modal>
      </Form>
    </section>
  );
}
